import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Fingerprint, Trash, Plus, KeyReturn, Copy, Check, Warning, Eye, EyeSlash } from '@phosphor-icons/react';

export default function PasskeysSection() {
  const { api, registerPasskey, passkeySupported } = useAuth();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Recovery codes state
  const [recStatus, setRecStatus] = useState(null);
  const [recCodes, setRecCodes] = useState(null);
  const [recRevealed, setRecRevealed] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const fetchAll = async () => {
    try {
      const [pk, rc] = await Promise.all([
        api.get('/auth/passkey/list'),
        api.get('/auth/recovery/status'),
      ]);
      setPasskeys(pk.data.passkeys || []);
      setRecStatus(rc.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const handleAdd = async () => {
    setError(''); setInfo('');
    const name = deviceName.trim() || guessDeviceName();
    setAdding(true);
    try {
      await registerPasskey(name);
      setInfo(`Passkey "${name}" added!`);
      setDeviceName('');
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to add passkey';
      setError(msg.includes('NotAllowed') ? 'Passkey enrollment cancelled' : msg);
    }
    setAdding(false);
  };

  const handleDelete = async (passkey_id) => {
    if (!window.confirm('Remove this passkey? You won\'t be able to use it for sign-in anymore.')) return;
    try {
      await api.delete(`/auth/passkey/${passkey_id}`);
      fetchAll();
    } catch (e) { setError('Failed to remove passkey'); }
  };

  const handleGenerateRecovery = async () => {
    if (recStatus?.unused > 0 && !confirmRegen) {
      setConfirmRegen(true);
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await api.post('/auth/recovery/generate');
      setRecCodes(res.data.codes);
      setRecRevealed(true);
      setConfirmRegen(false);
      fetchAll();
    } catch (e) { setError('Failed to generate codes'); }
    setLoading(false);
  };

  const copyAllCodes = () => {
    if (!recCodes) return;
    navigator.clipboard.writeText(recCodes.join('\n'));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  if (!passkeySupported) {
    return (
      <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-sm text-yellow-300">
        <Warning size={16} className="inline mr-2" />
        Your browser doesn't support passkeys. Try Chrome, Safari 16+, or Edge on a recent device.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="passkeys-section">
      {/* Passkeys */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
              <Fingerprint size={16} weight="duotone" className="text-[#00E5FF]" /> Passkeys
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Sign in with Face ID, Touch ID, Windows Hello, or a security key
            </p>
          </div>
        </div>

        {/* Add form */}
        <div className="flex gap-2 mb-3">
          <input type="text" value={deviceName} onChange={e => setDeviceName(e.target.value)}
            placeholder={guessDeviceName()} data-testid="passkey-device-name"
            className="flex-1 px-3 py-2 rounded-xl bg-background border border-input text-sm outline-none" />
          <button onClick={handleAdd} disabled={adding} data-testid="add-passkey-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-semibold disabled:opacity-50">
            {adding ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus size={14} />}
            Add this device
          </button>
        </div>

        {error && <div className="p-2.5 mb-2 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400">{error}</div>}
        {info && <div className="p-2.5 mb-2 rounded-xl bg-green-500/10 border border-green-500/20 text-xs text-green-400">{info}</div>}

        {/* List */}
        {passkeys.length === 0 ? (
          <p className="text-xs text-muted-foreground italic px-2 py-3">No passkeys registered yet.</p>
        ) : (
          <div className="space-y-2">
            {passkeys.map(pk => (
              <div key={pk.passkey_id} data-testid={`passkey-${pk.passkey_id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                  <Fingerprint size={16} weight="duotone" className="text-[#00E5FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{pk.device_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    Added {formatDate(pk.created_at)}
                    {pk.last_used_at && <> · Last used {formatDate(pk.last_used_at)}</>}
                  </p>
                </div>
                <button onClick={() => handleDelete(pk.passkey_id)} className="text-muted-foreground hover:text-red-400 p-1.5">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recovery Codes */}
      <div className="pt-5 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
              <KeyReturn size={16} weight="duotone" className="text-[#FF6B35]" /> Recovery Codes
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {recStatus
                ? `${recStatus.unused} of ${recStatus.total} codes remaining`
                : 'Use these if you ever lose access to your devices'}
            </p>
          </div>
        </div>

        {confirmRegen && (
          <div className="p-3 mb-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-300">
            <Warning size={14} className="inline mr-1" />
            Generating new codes will INVALIDATE all existing recovery codes. Continue?
            <div className="flex gap-2 mt-2">
              <button onClick={handleGenerateRecovery} className="px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-300 text-xs font-semibold">Yes, regenerate</button>
              <button onClick={() => setConfirmRegen(false)} className="px-3 py-1.5 rounded-lg bg-white/5 text-muted-foreground text-xs font-semibold">Cancel</button>
            </div>
          </div>
        )}

        {recCodes ? (
          <div className="p-4 rounded-xl bg-[#FF6B35]/5 border border-[#FF6B35]/30" data-testid="recovery-codes-display">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Warning size={14} weight="fill" className="text-[#FF6B35]" />
                <span className="text-xs font-bold text-[#FF6B35] uppercase tracking-wider">Save these now — won't be shown again</span>
              </div>
              <button onClick={() => setRecRevealed(!recRevealed)} className="text-xs text-muted-foreground hover:text-foreground">
                {recRevealed ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3 font-mono">
              {recCodes.map((c, i) => (
                <div key={i} className="px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground tracking-wide">
                  {recRevealed ? c : '•'.repeat(c.length)}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={copyAllCodes} data-testid="copy-codes-btn"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-semibold">
                {copiedAll ? <Check size={14} /> : <Copy size={14} />}
                {copiedAll ? 'Copied!' : 'Copy all codes'}
              </button>
              <button onClick={() => setRecCodes(null)} className="px-4 py-2 rounded-xl bg-white/5 text-muted-foreground text-xs font-semibold">
                I've saved them
              </button>
            </div>
          </div>
        ) : (
          <button onClick={handleGenerateRecovery} disabled={loading} data-testid="generate-recovery-btn"
            className="w-full py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-border text-sm font-semibold text-foreground disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <div className="w-3 h-3 border-2 border-foreground border-t-transparent rounded-full animate-spin" /> : <KeyReturn size={14} />}
            {recStatus?.total > 0 ? 'Regenerate Recovery Codes' : 'Generate 8 Recovery Codes'}
          </button>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function guessDeviceName() {
  const ua = navigator.userAgent || '';
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/Android.*Mobile/.test(ua)) return 'Android Phone';
  if (/Android/.test(ua)) return 'Android Tablet';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux PC';
  return 'My Device';
}
